// Read-only failover. Wallet signing and broadcasts never use these endpoints.
export const BASE_BACKUPS = ['https://base-rpc.publicnode.com', 'https://base.drpc.org'];
const transient = error => error.retryable || /rate limit|request limit|too many requests|timeout|timed out|header not found|unknown block|missing trie|429/i.test(error.message || '');
export class ReadProvider {
  constructor(config, fetcher = (...args) => fetch(...args)) {
    this.fetcher = fetcher;
    this.chainId = Number(config.chainId);
    this.endpoints = [...new Set([config.rpc, ...(this.chainId === 8453 ? BASE_BACKUPS : [])])].map(url => ({url, until:0, verified:null}));
    this.preferred = 0;
    this.sequence = 0;
  }
  async call(endpoint, method, params) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 8000);
    try {
      let response;
      try {
        response = await this.fetcher(endpoint.url, {method:'POST', headers:{'content-type':'application/json'}, body:JSON.stringify({jsonrpc:'2.0',id:++this.sequence,method,params}), signal:controller.signal});
      } catch (error) { error.retryable = true; throw error; }
      if (!response.ok) throw Object.assign(new Error(`RPC HTTP ${response.status}`), {retryable:true});
      let body;
      try { body = await response.json(); } catch { throw Object.assign(new Error('Invalid RPC response'), {retryable:true}); }
      if (body.error) throw Object.assign(new Error(body.error.message || 'RPC error'), body.error,
        {retryable: [-32005,-32603,-32601,429].includes(Number(body.error.code))});
      if (!Object.prototype.hasOwnProperty.call(body,'result')) throw Object.assign(new Error('Incomplete RPC response'), {retryable:true});
      return body.result;
    } finally { clearTimeout(timer); }
  }
  async request({method, params=[]}) {
    if (!/^(eth_(call|get[A-Za-z]+|chainId|blockNumber|gasPrice|feeHistory|maxPriorityFeePerGas|estimateGas)|net_version)$/.test(method))
      throw new Error('Read-only RPC: unsupported method');
    let last;
    const order = this.endpoints.map((_,i)=>(this.preferred+i)%this.endpoints.length);
    for (const index of order) {
      const endpoint = this.endpoints[index];
      if (endpoint.until > Date.now()) continue;
      try {
        if (!endpoint.verified) endpoint.verified = this.call(endpoint,'eth_chainId',[]).then(id=>{
          if (Number(id)!==this.chainId) throw Object.assign(new Error('RPC network mismatch'),{retryable:true});
        });
        await endpoint.verified;
        const result = await this.call(endpoint,method,params);
        this.preferred = index;
        return result;
      } catch(error) {
        if (!transient(error)) throw error;
        endpoint.until = Date.now()+60000;
        endpoint.verified = null;
        last = error;
      }
    }
    throw last || new Error('Network temporarily unavailable; retrying shortly.');
  }
}
